/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxfrag */
import React from 'react';

import { css, jsx, styled } from '@compiled/react';
import ReactDOM from 'react-dom';

import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator as DropIndicatorUI } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	TAB_ANGLE,
	TAB_GAP,
	TAB_HEIGHT,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_BORDER_SELECTED_VAR,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_ELEVATION_SURFACE_VAR,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_TEXT_VAR,
} from '@atlassian/navigation-kit-common';

import type { TabDragState } from '../../../types';
import { isSafari } from '../../utils';

const DropIndicator = ({ closestEdge, testId }: { closestEdge?: Edge | null; testId?: string }) =>
	(closestEdge && (
		<DropIndicatorWrapper data-testid={testId}>
			<DropIndicatorUI edge={closestEdge} gap={`${TAB_GAP}px`} />
		</DropIndicatorWrapper>
	)) ??
	null;

export const DragPreview = ({
	elemAfter,
	name,
}: {
	elemAfter?: React.ReactNode;
	name: React.ReactNode;
}) => (
	<div
		css={[previewStyles, !isSafari() && tiltStyles]}
		data-testid="navigation-kit-ui-tab-common.ui.after-elements.drag-preview"
	>
		<MoreIcon color="currentColor" label="" LEGACY_size="small" />
		<span>{name}</span>
		{elemAfter && <Box xcss={elemAfterContainerStyle}>{elemAfter}</Box>}
	</div>
);

const AfterElements = ({
	elemAfter,
	closestEdge,
	dragState,
	name,
}: {
	closestEdge?: Edge | null;
	dragState?: TabDragState;
	elemAfter?: React.ReactNode;
	name: React.ReactNode;
}) => (
	<>
		{elemAfter && <Box xcss={elemAfterContainerStyle}>{elemAfter}</Box>}
		<DropIndicator
			testId="navigation-kit-ui-tab-common.ui.after-elements.drag-indicator"
			closestEdge={closestEdge}
		/>
		{dragState && dragState.type === 'preview'
			? ReactDOM.createPortal(
					<DragPreview elemAfter={elemAfter} name={name} />,
					dragState.container,
				)
			: null}
	</>
);

const elemAfterContainerStyle = xcss({
	display: 'flex',
});

const tiltStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	rotate: `${TAB_ANGLE}deg`,
});

const previewStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050', '4px'),
	borderInline: `${token('border.width', '1px')} solid ${token('color.border', 'grey')}`,
	borderTopLeftRadius: token('radius.large', '8px'),
	borderTopRightRadius: token('radius.large', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	color: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_TEXT_VAR}, ${token('color.text')})`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_ELEVATION_SURFACE_VAR}, ${token('elevation.surface')})`,
	paddingTop: 0,
	paddingRight: token('space.100', '8px'),
	paddingBottom: 0,
	paddingLeft: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	height: `${TAB_HEIGHT}px`,
	'--display-icon-before': 'flex',
});

export default AfterElements;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropIndicatorWrapper = styled.div({
	position: 'absolute',
	top: token('space.050', '4px'),
	left: 0,
	width: '100%',
	height: 'calc(100% - 4px)',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'--ds-border-selected': `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_BORDER_SELECTED_VAR})`,
});
